import { trackEvent } from '../../services/analytics';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Modal,
    Snackbar,
    Button,
    Stack,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HelpIcon from '@mui/icons-material/Help';
import TermsIcon from '@mui/icons-material/Description';
import LockIcon from '@mui/icons-material/Lock';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
// import RestaurantIcon from '@mui/icons-material/Restaurant';
import StarIcon from '@mui/icons-material/Star';
import TutorialIcon from '@mui/icons-material/QuestionAnswer';
import CampaignIcon from '@mui/icons-material/Campaign';
import TopAppBar from '../layout/TopAppBar';
import { PageContainer, SectionHeader, SurfaceCard } from '../ui/ConvivialComponents';
import { deleteCurrentUserAccount, logout } from '../../services/firebaseData';
import { logoutViaNativeApp, requestNativeLogin, isConvivialNativeAppWebView, postConvivialNativeMessage } from '../../services/nativeAppBridge';

export default function SettingsPage() {
    const navigate = useNavigate();
    const userId = localStorage.getItem('user_id');
    const [modalOpen, setModalOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarMessageInfo, setSnackbarMessageInfo] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    useEffect(() => {
        if (!userId) {
            const handledByNative = requestNativeLogin();

            if (!handledByNative) {
                navigate('/');
            }

            return;
        }
    }, [userId, navigate]);

    const handleDeletion = async () => {
        setIsDeleting(true);
        try {
            await deleteCurrentUserAccount();

            setSnackbarMessageInfo('Account eliminato.');
            setModalOpen(false);

            if (isConvivialNativeAppWebView()) {
                postConvivialNativeMessage({
                    type: 'accountDeleted',
                });
                return;
            }

            navigate('/');
        } catch (error) {
            console.error('Error deleting:', error);
            if (error.code === 'auth/popup-closed-by-user') {
                setSnackbarMessage('Eliminazione annullata: accesso Google non completato.');
            } else if (error.code === 'auth/requires-recent-login') {
                setSnackbarMessage('Per eliminare l’account devi confermare di nuovo l’accesso Google.');
            } else {
                setSnackbarMessage('Errore di autenticazione. Riprova. Contattaci se il problema persiste.');
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            // if running in webviewggl will call the native app to logout, if not it will call the standard logout() function
            const handledByNative = await logoutViaNativeApp(logout);
            trackEvent('logout');

            if (handledByNative) {
                return;
            }

            navigate('/');
        } catch (error) {
            console.error('Error logging out:', error);
            setSnackbarMessage('Non siamo riusciti a fare logout. Riprova.');
        } finally {
            setIsLoggingOut(false);
        }
    };

    const accountRows = [
        {
            icon: <LocationOnIcon />,
            title: 'Partecipazioni',
            body: 'Tutti i tavoli collegati al tuo profilo.',
            onClick: () => navigate(`/user/${userId}/tables`),
        },
        {
            icon: <StarIcon />,
            title: 'Feedback ricevuti',
            body: 'Valutazioni lasciate dagli altri convivial.',
            onClick: () => navigate('/feedbacks-received'),
        },
        {
            icon: <StarIcon />,
            title: 'Feedback inviati',
            body: 'Le valutazioni che hai condiviso.',
            onClick: () => navigate('/feedbacks-sent'),
        },
        {
            icon: <TutorialIcon />,
            title: 'Tutorial Convivial',
            body: 'Rivedi la guida introduttiva.',
            onClick: () => navigate('/tutorial', { state: { replay: true } }),
        },
        {
            icon: <CampaignIcon />,
            title: 'Privacy e comunicazioni',
            body: 'Gestisci newsletter e preferenze di marketing.',
            onClick: () => navigate('/privacy-preferences'),
        },
    ];

    const supportRows = [
        {
            icon: <HelpIcon />,
            title: 'Aiuto e supporto',
            body: 'Apri il centro assistenza Convivial.',
            onClick: () => { window.location.href = 'https://www.convivialapp.it/supporto/'; },
        },
        {
            icon: <TermsIcon />,
            title: 'Termini e condizioni',
            body: 'Leggi i termini di utilizzo di Convivial.',
            onClick: () => { window.location.href = 'https://www.convivialapp.it/tc.html'; },
        },
        {
            icon: <TermsIcon />,
            title: 'Cookie Policy',
            body: 'Informazioni sui cookie utilizzati.',
            onClick: () => { window.location.href = 'https://www.iubenda.com/privacy-policy/93045104/cookie-policy'; },
        },
        {
            icon: <LockIcon />,
            title: 'Privacy',
            body: 'Leggi la privacy policy.',
            onClick: () => { window.location.href = 'https://www.iubenda.com/privacy-policy/93045104'; },
        },
        // {
        //     icon: <RestaurantIcon />,
        //     title: 'Sei un ristoratore?',
        //     body: 'Scopri le opportunita per i locali.',
        //     onClick: () => { window.location.href = 'https://convivialapp.it/ristoratore'; },
        // },
    ];

    return (
        <>
            <TopAppBar title="ConvivialApp.it" showAvatar />
            <PageContainer maxWidth="900px" sx={{ pt: 'calc(var(--cv-top-bar-height) + 32px)' }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h1" sx={{ color: 'primary.main', fontSize: { xs: 28, md: 34 } }}>
                            Impostazioni
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 620 }}>
                            Gestisci profilo, storico, privacy e accesso al tuo account.
                        </Typography>
                    </Box>

                    <SurfaceCard>
                        <SectionHeader icon={<LocationOnIcon />} title="Account" />
                        <Stack spacing={1.25}>
                            {accountRows.map((row) => (
                                <SettingsRow key={row.title} {...row} />
                            ))}
                        </Stack>
                    </SurfaceCard>

                    <SurfaceCard>
                        <SectionHeader icon={<HelpIcon />} title="Supporto e documenti" />
                        <Stack spacing={1.25}>
                            {supportRows.map((row) => (
                                <SettingsRow key={row.title} {...row} />
                            ))}
                        </Stack>
                    </SurfaceCard>

                    <SurfaceCard sx={{ borderColor: 'rgba(186,26,26,0.22)', bgcolor: 'rgba(255,218,214,0.22)' }}>
                        <SectionHeader icon={<LockIcon />} title="Accesso" />
                        <Stack spacing={1.25}>
                            <SettingsRow
                                icon={<LogoutIcon />}
                                title="Logout"
                                body="Esci da questo dispositivo."
                                onClick={handleLogout}
                                disabled={isLoggingOut}
                            />
                            <SettingsRow
                                icon={<DeleteForeverIcon />}
                                title="Elimina account"
                                body="Rimuove definitivamente il tuo profilo."
                                onClick={() => setModalOpen(true)}
                                danger
                            />
                        </Stack>
                    </SurfaceCard>
                </Stack>
            </PageContainer>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'rgba(28,28,26,0.24)', backdropFilter: 'blur(8px)' }}>
                    <SurfaceCard sx={{ width: '100%', maxWidth: 430 }}>
                        <Typography variant="h2" sx={{ color: 'error.main', mb: 1 }}>
                            Elimina account
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Questa azione elimina definitivamente il tuo profilo, i dati di accesso e le informazioni collegate al tuo account ConvivialApp.it.
                            Per sicurezza ti chiederemo di confermare l'accesso Google prima di completare l'eliminazione.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <Button
                                variant="outlined"
                                onClick={() => setModalOpen(false)}
                                disabled={isDeleting}
                                sx={{ borderRadius: 3, minHeight: 48, fontWeight: 600 }}
                                fullWidth
                            >
                                Annulla
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={handleDeletion}
                                disabled={isDeleting}
                                sx={{ borderRadius: 3, minHeight: 48, fontWeight: 600 }}
                                fullWidth
                            >
                                {isDeleting ? 'Eliminazione...' : 'Elimina account'}
                            </Button>
                        </Stack>
                    </SurfaceCard>
                </Box>
            </Modal>

            <Snackbar open={snackbarMessage !== ''} autoHideDuration={6000} onClose={() => setSnackbarMessage('')}>
                <MuiAlert onClose={() => setSnackbarMessage('')} elevation={6} variant="filled" severity="error" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>

            <Snackbar open={snackbarMessageInfo !== ''} autoHideDuration={6000} onClose={() => setSnackbarMessageInfo('')}>
                <MuiAlert onClose={() => setSnackbarMessageInfo('')} elevation={6} variant="filled" severity="success" sx={{ width: '100%' }}>
                    {snackbarMessageInfo}
                </MuiAlert>
            </Snackbar>
        </>
    );
}

function SettingsRow({ icon, title, body, onClick, danger = false, disabled = false }) {
    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={disabled ? undefined : onClick}
            onKeyDown={(event) => {
                if (!disabled && (event.key === 'Enter' || event.key === ' ')) onClick();
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: danger ? 'rgba(186,26,26,0.24)' : 'rgba(223,191,193,0.35)',
                bgcolor: 'background.surfaceLow',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                transition: 'transform 160ms ease, background-color 160ms ease',
                '&:hover': { bgcolor: danger ? 'rgba(255,218,214,0.5)' : 'background.surfaceHigh' },
                '&:active': { transform: disabled ? 'none' : 'scale(0.99)' },
            }}
        >
            <Box sx={{ color: danger ? 'error.main' : 'secondary.main', display: 'grid', placeItems: 'center' }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontSize: 16, color: danger ? 'error.main' : 'text.primary' }}>
                    {title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {body}
                </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: danger ? 'error.main' : 'text.secondary' }} />
        </Box>
    );
}
